import {
  AssetTypeSpendDataDto,
  GetPurchasedAssetsSpendWidgetRequest,
  PurchasedAssetsSpendWidgetClient,
} from "@lib/ShiOneClient";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { Box, Skeleton } from "@mui/material";
import { graphColors } from "shared-ui";
import { useApi } from "@utils/useApi";
import { useQuery } from "@tanstack/react-query";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { formatShortNumber } from "../AssetsSpendWidget";
import { DateTime } from "luxon";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

const chartOptions = () => ({
  responsive: true,
  plugins: {
    legend: { display: true, position: "bottom" as const },
    datalabels: {
      display: false,
    },
  },
  scales: {
    x: { stacked: true },
    y: {
      stacked: true,
      ticks: {
        callback: function (value: number | string) {
          const num =
            typeof value === "string" ? Number.parseFloat(value) : value;
          return `$${formatShortNumber(num)}`;
        },
      },
    },
  },
});

function getPrevQuarter(year: number, quarter: number, n: number) {
  let quarters = [];
  for (let i = 0; i < n; i++) {
    quarters.push({ year, quarter });
    quarter--;
    if (quarter === 0) {
      quarter = 4;
      year--;
    }
  }
  return quarters;
}

function recentSixQtrs(data: AssetTypeSpendDataDto[]) {
  if (!data || data.length === 0) return [];
  const sorted = sortByYearMonth(data);
  const last = sorted.at(-1);
  const currentYear = last?.year ?? 0;
  const currentMonth = last?.month ?? 0;
  const currentQuarter = monQtr(currentMonth);
  const quarters = getPrevQuarter(currentYear, currentQuarter, 6);
  const quarterSet = new Set(quarters.map((q) => `${q.year}-Q${q.quarter}`));
  return sorted.filter((entry) => {
    const year = entry.year ?? 0;
    const month = entry.month ?? 0;
    const quarter = monQtr(month);
    const key = `${year}-Q${quarter}`;
    return quarterSet.has(key);
  });
}

function sortByYearMonth(data: AssetTypeSpendDataDto[]) {
  return [...data].sort((a, b) => {
    const yearA = a?.year ?? 0;
    const yearB = b?.year ?? 0;
    if (yearA !== yearB) {
      return yearA - yearB;
    }
    const monthA = a?.month ?? 0;
    const monthB = b?.month ?? 0;
    return monthA - monthB;
  });
}

function monQtr(month: number): number {
  if (month >= 1 && month <= 3) return 1;
  if (month >= 4 && month <= 6) return 2;
  if (month >= 7 && month <= 9) return 3;
  return 4;
}

function aggregateByQuarter(
  data: AssetTypeSpendDataDto[],
): Record<string, Record<string, number>> {
  const result: Record<string, Record<string, number>> = {};
  for (const entry of data) {
    const year = entry.year ?? 0;
    const quarter = monQtr(entry.month ?? 0);
    const groupKey = `${year}-Q${quarter}`;
    if (!result[groupKey]) {
      result[groupKey] = {};
    }
    const assetType = entry.assetType;
    if (!assetType) continue;
    if (!result[groupKey][assetType]) {
      result[groupKey][assetType] = 0;
    }
    result[groupKey][assetType] += entry.amount ?? 0;
  }
  return result;
}

const prepareChartData = (
  aggregatedData: Record<string, Record<string, number>>,
) => {
  const keys = Object.keys(aggregatedData);
  const labels = keys.map((key) => {
    const [year, quarter] = key.split("-Q");
    return `Q${quarter} ${year}`;
  });
  const assetTypesSet = new Set<string>();
  for (const byType of Object.values(aggregatedData)) {
    for (const type of Object.keys(byType)) {
      assetTypesSet.add(type);
    }
  }
  const assetTypes = Array.from(assetTypesSet);
  const datasets = assetTypes.map((type, idx) => ({
    label: type,
    data: keys.map((key) => aggregatedData[key][type] || 0),
    backgroundColor: graphColors[idx % graphColors.length],
  }));
  return { labels, datasets };
};

const useGetSpendByAssetType = () => {
  const request = {
    clientLocalNow: DateTime.local().toISO(),
  } as unknown as GetPurchasedAssetsSpendWidgetRequest;
  const api = useApi(PurchasedAssetsSpendWidgetClient);

  return useQuery({
    queryKey: ["purchased-assets-spend-widget", "by-asset-type"],
    queryFn: () => api.getSpendByAssetType(request),
  });
};

export const AssetsSpendByType = () => {
  const { data: response, isLoading } = useGetSpendByAssetType();
  const rawSpendData = response?.data?.assetTypeSpendData || [];
  const sortedRawSpendData = sortByYearMonth(rawSpendData);
  const sixQtrSpend = recentSixQtrs(sortedRawSpendData);
  const aggregateByQtr = aggregateByQuarter(sixQtrSpend);
  const chartData = prepareChartData(aggregateByQtr);

  if (isLoading) {
    return <Skeleton variant="rectangular" width="100%" height={140} />;
  }
  if (!sixQtrSpend.length) {
    return (
      <Box display="flex" sx={{ p: 2, justifyContent: "space-between" }}>
        <Box sx={{ fontWeight: 500 }}>Spend by Asset Type</Box>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 2,
        pb: 6,
        width: "100%",
        height: 350,
      }}
    >
      <Box sx={{ fontWeight: "bold", mb: 2 }}>Spend by Asset Type</Box>
      <Bar
        data={chartData}
        options={chartOptions()}
        plugins={[ChartDataLabels]}
      />
    </Box>
  );
};

export default AssetsSpendByType;
